import { WEBSITE_DOMAIN } from '@application/constants';
import type { Country, Language, Route } from '@application/types';
import {
  countryCodeFromCountry,
  countryFromPath,
  infoFromPath,
  languageFromPath,
  removeTrailingSlash,
} from '@application/utils';
import { CountryCode, LanguageCode } from '@gql/graphql';
import cleanDeep from 'clean-deep';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import type { LocationDescriptorObject } from 'history';
import { stringify } from 'qs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';

export type UseRouterReturn = ReturnType<typeof useRouter>;

export interface NavigateToRouteOptions {
  newLanguage?: Language;
  newCountry?: Country;
  params?: string[];
  search?: any;
  state?: any;
}

export const useRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const language = useMemo(() => languageFromPath(location.pathname), [location.pathname]);

  const country = useMemo(() => countryFromPath(location.pathname), [location.pathname]);

  const countryCode = useMemo(() => countryCodeFromCountry(country), [country]);

  const languageCode = useMemo(() => {
    if (language === 'fr' && country === 'be') {
      return LanguageCode.FR_BE;
    }

    if (language === 'nl' && country === 'be') {
      return LanguageCode.NL_BE;
    }

    if (language === 'fr' && country === 'fr') {
      return LanguageCode.FR_FR;
    }

    if (language === 'nl' && country === 'nl') {
      return LanguageCode.NL_NL;
    }

    if (language === 'de' && country === 'de') {
      return LanguageCode.DE_DE;
    }
    if (language === 'en' && country === 'de') {
      return LanguageCode.EN_DE;
    }

    return LanguageCode.FR_BE;
  }, [country, language]);

  const infoFromCurrentPath = useMemo(() => {
    const path = location.pathname;
    const routes = t('routes', { returnObjects: true, lng: language });
    return infoFromPath(path, language, country, routes, location);
  }, [language, country, location, t]);

  const translatedRoute = useCallback(
    (route: Route, newLanguage: Language = language, newCountry: Country = country) => {
      // This enable us to use cimode
      if (!route) {
        return '';
      }

      const pathWithoutParams = route.split('/')[0];
      const params = route?.split('/')?.slice(1)?.join?.('/') ?? '';
      const translated = `${t(`routes.${pathWithoutParams}`, { lng: newLanguage })}`;
      const translatedPart = !translated ? '' : `${translated}/`;
      const total = `/${newCountry}/${newLanguage}/${translatedPart}${params}`;
      return total;
    },
    [country, language, t],
  );

  const setCurrentSearch = useCallback(
    (searchObject: any) => {
      navigate({
        pathname: location.pathname,
        search: `?${stringify(cleanDeep(searchObject))}`,
      });
    },
    [navigate, location.pathname],
  );

  const routeLocationDescriptor = useCallback(
    (route: Route, options?: NavigateToRouteOptions): LocationDescriptorObject => {
      const {
        newLanguage,
        newCountry,
        params = [],
        search = {},
        state = infoFromCurrentPath.state,
      } = options ?? {};
      const path = translatedRoute(route, newLanguage, newCountry);
      if (!path) {
        return {};
      }

      const pathnameForPathWithParams = `${[removeTrailingSlash(path), ...params].join('/')}/`;
      const pathname = !params.length ? path : pathnameForPathWithParams;
      return {
        pathname,
        search: `?${stringify(cleanDeep(search))}`,
        state,
      };
    },
    [infoFromCurrentPath.state, translatedRoute],
  );

  const navigateToRoute = useCallback(
    (route: Route, options?: NavigateToRouteOptions) => {
      return navigate(routeLocationDescriptor(route, options));
    },
    [navigate, routeLocationDescriptor],
  );

  const setState = useCallback(
    (newState: any) => {
      navigate(location.pathname, {
        state: newState,
      });
    },
    [location.pathname, navigate],
  );

  const getLocaleLinksForTranslations = useCallback(
    (
      translations:
        | Array<
            Record<string, any> & {
              language: string | null | undefined;
              url: string | null | undefined;
            }
          >
        | null
        | undefined,
      onlyIncountryCode?: CountryCode | null,
    ): Partial<Record<Country, Partial<Record<Language, string | undefined>>>> => {
      const generateForLocale = (lang: Language, count: Country) => {
        const translatedUrl =
          (translations ?? []).find((x) => x.language === lang)?.url ?? undefined;

        const route = routeLocationDescriptor(infoFromCurrentPath.route, {
          newLanguage: lang,
          newCountry: count,
          params: translatedUrl ? [translatedUrl] : infoFromCurrentPath.params,
          search: infoFromCurrentPath.search,
          ...(infoFromCurrentPath?.state ? { state: infoFromCurrentPath?.state } : {}),
        });

        const pathname: string = (route as any)?.pathname;
        if (!pathname) return;
        return `${
          typeof window !== 'undefined' && window?.location?.origin
            ? window.location.origin
            : WEBSITE_DOMAIN
        }${pathname}`;
      };

      switch (onlyIncountryCode) {
        case CountryCode.BE:
          return {
            be: {
              fr: generateForLocale('fr', 'be'),
              nl: generateForLocale('nl', 'be'),
            },
          };
        case CountryCode.FR:
          return {
            fr: {
              fr: generateForLocale('fr', 'fr'),
            },
          };
        case CountryCode.DE:
          return {
            de: {
              de: generateForLocale('de', 'de'),
              en: generateForLocale('en', 'de'),
            },
          };
        case CountryCode.NL:
          return {
            nl: {
              nl: generateForLocale('nl', 'nl'),
            },
          };
        case CountryCode.CH:
          return {
            ch: {
              fr: generateForLocale('fr', 'ch'),
              de: generateForLocale('de', 'ch'),
            },
          };
        default:
          return {
            be: {
              fr: generateForLocale('fr', 'be'),
              nl: generateForLocale('nl', 'be'),
            },
            fr: {
              fr: generateForLocale('fr', 'fr'),
            },
            nl: {
              nl: generateForLocale('nl', 'nl'),
            },
            de: {
              de: generateForLocale('de', 'de'),
              en: generateForLocale('en', 'de'),
            },
            ch: {
              fr: generateForLocale('fr', 'ch'),
              de: generateForLocale('de', 'ch'),
            },
          };
      }
    },
    [
      infoFromCurrentPath.params,
      infoFromCurrentPath.route,
      infoFromCurrentPath.search,
      infoFromCurrentPath?.state,
      routeLocationDescriptor,
    ],
  );

  return {
    language,
    country,
    countryCode,
    languageCode,
    translatedRoute,
    navigateToRoute,
    infoFromCurrentPath,
    routeLocationDescriptor,
    translate: t,
    setCurrentSearch,
    setState,
    getLocaleLinksForTranslations,
    goBack: () => navigate(-1),
  };
};
